import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db, collection, addDoc, serverTimestamp, doc, getDoc } from '../services/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer.jsx';

const CheckoutPage = () => {
  const { cartItems, totalPrice, clearCart, updateQuantity } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [authError, setAuthError] = useState('');
  const [deliverySettings, setDeliverySettings] = useState({
    deliveryFee: 30,
    freeDeliveryThreshold: 100
  });
  
  // Mobile detection and step management
  const [isMobile, setIsMobile] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
    
  useEffect(() => {
    // Fetch delivery settings
    const fetchDeliverySettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setDeliverySettings({
            deliveryFee: data.deliveryFee || 30,
            freeDeliveryThreshold: data.freeDeliveryThreshold || 100
          });
        }
      } catch (err) {
        console.error('Error fetching delivery settings:', err);
      }
    };

    // Fetch settings and simulate loading time
    Promise.all([
      fetchDeliverySettings(),
      new Promise(resolve => setTimeout(resolve, 1000)) // Simulate loading time
    ]).then(() => {
      setLoading(false);
    });
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: currentUser?.email || '',
    phone: '',
    deliveryMethod: 'delivery',
    pickupBranch: '',
    paymentMethod: 'Online Payment',
    comment: ''
  });
  const [errors, setErrors] = useState({});

  // Calculate delivery fee based on total price and settings
  const deliveryFee = formData.deliveryMethod === 'delivery' && totalPrice >= deliverySettings.freeDeliveryThreshold ? 0 : (formData.deliveryMethod === 'delivery' ? deliverySettings.deliveryFee : 0);

  // Step navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 1: // Contact Information
        if (!formData.fullName.trim()) newErrors.fullName = 'Please enter your full name';
        else if (formData.fullName.trim().length < 2) newErrors.fullName = 'Name must be at least 2 characters';
        
        if (!formData.email.trim()) newErrors.email = 'Email address is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
        
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        else if (!/^[+]?[0-9\s\-\(\)]{8,}$/.test(formData.phone.trim())) newErrors.phone = 'Please enter a valid phone number';
        break;
        
      case 2: // Delivery Details
        if (formData.deliveryMethod === 'pickup' && !formData.pickupBranch) {
          newErrors.pickupBranch = 'Please select a pickup location';
        }
        break;
        
      case 3: // Payment Method
        if (!formData.paymentMethod) {
          newErrors.paymentMethod = 'Please select a payment method';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Enhanced validation with better error messages
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Please enter your full name';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[0-9\s\-\(\)]{8,}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (formData.deliveryMethod === 'pickup' && !formData.pickupBranch) {
      newErrors.pickupBranch = 'Please select a pickup location';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as Qatar number: +974 XXXX XXXX
    if (phoneNumber.length <= 8) {
      return phoneNumber.replace(/(\d{4})(\d{0,4})/, (match, p1, p2) => {
        return p2 ? `${p1} ${p2}` : p1;
      });
    }
    
    return phoneNumber.slice(0, 8).replace(/(\d{4})(\d{4})/, '$1 $2');
  };

  const formatName = (value) => {
    // Capitalize first letter of each word and remove extra spaces
    return value
      .replace(/[^a-zA-Z\s]/g, '') // Remove non-alphabetic characters except spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  const validateEmailReal = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Apply auto-formatting based on field type
    switch (name) {
      case 'fullName':
        formattedValue = formatName(value);
        break;
      case 'phone':
        formattedValue = formatPhoneNumber(value);
        break;
      case 'email':
        formattedValue = value.toLowerCase().trim();
        break;
      default:
        break;
    }
    
    setFormData({ ...formData, [name]: formattedValue });
    
    // Real-time validation
    if (errors[name]) {
      const newErrors = { ...errors };
      
      // Clear error if field becomes valid
      switch (name) {
        case 'fullName':
          if (formattedValue.trim().length >= 2) {
            delete newErrors[name];
          }
          break;
        case 'email':
          if (validateEmailReal(formattedValue)) {
            delete newErrors[name];
          }
          break;
        case 'phone':
          if (formattedValue.replace(/\s/g, '').length >= 8) {
            delete newErrors[name];
          }
          break;
        default:
          delete newErrors[name];
          break;
      }
      
      setErrors(newErrors);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isProcessing) return; // Prevent multiple submissions

    setIsProcessing(true);
    setLoading(true);
    try {
      // NOTE: In a real application, you would process the payment here with a payment gateway.
      const orderData = {
        userId: currentUser?.uid || null,
        userName: formData.fullName,
        userEmail: formData.email,
        shipping: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        },
        deliveryMethod: formData.deliveryMethod,
        pickupBranch: formData.pickupBranch,
        paymentMethod: formData.paymentMethod,
        comment: formData.comment,
        items: cartItems,
        subtotal: totalPrice,
        deliveryFee: deliveryFee,
        total: totalPrice + deliveryFee,
        status: 'pending',
        createdAt: serverTimestamp()
      };
      console.log('[CheckoutPage] orderData to be sent:', orderData);

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      console.log('[Checkout] Order created with ID:', docRef.id);
      // Trigger WhatsApp notification (admin and customer)
      try {
        const idToken = currentUser && currentUser.getIdToken ? await currentUser.getIdToken() : null;
        const notificationPayload = {
          orderId: docRef.id,
          orderData
        };
        const response = await fetch('https://us-central1-greycoffee-f7f71.cloudfunctions.net/sendOrderNotification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
          },
          body: JSON.stringify(notificationPayload)
        });
        if (!response.ok) {
          throw new Error(`[Checkout] Notification HTTP error! status: ${response.status}`);
        }
        console.log('[Checkout] WhatsApp notification sent successfully');
      } catch (notifyErr) {
        console.error('[Checkout] Failed to send WhatsApp notification:', notifyErr);
      }
      clearCart();
      navigate(`/order-success?orderId=${docRef.id}`);
    } catch (err) {
      console.error(err);
      setErrors({ form: 'Failed to place order. Please try again.' });
    }
    setLoading(false);
    setIsProcessing(false);
  };

  // Step components for mobile
  const StepProgressBar = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '20px 0 40px',
      padding: '0 20px'
    }}>
      {[1, 2, 3, 4].map(step => (
        <React.Fragment key={step}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: step <= currentStep ? '#333' : '#e8e8e8',
            color: step <= currentStep ? '#fff' : '#999',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {step}
          </div>
          {step < 4 && (
            <div style={{
              width: '40px',
              height: '2px',
              background: step < currentStep ? '#333' : '#e8e8e8',
              margin: '0 8px'
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const StepHeader = ({ title, subtitle }) => (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      <h2 style={{
        fontSize: '24px',
        fontWeight: '600',
        margin: '0 0 8px 0',
        color: '#181818'
      }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{
          fontSize: '14px',
          color: '#666',
          margin: '0'
        }}>
          {subtitle}
        </p>
      )}
    </div>
  );

  const StepButtons = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      margin: '40px 0 20px',
      padding: '0 20px'
    }}>
      <button
        type="button"
        onClick={prevStep}
        disabled={currentStep === 1}
        style={{
          padding: '12px 24px',
          background: 'transparent',
          border: '1px solid #e8e8e8',
          borderRadius: '0',
          color: currentStep === 1 ? '#ccc' : '#333',
          fontSize: '14px',
          fontWeight: '600',
          cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}
      >
        Back
      </button>
      
      {currentStep < totalSteps ? (
        <button
          type="button"
          onClick={() => {
            if (validateCurrentStep()) {
              nextStep();
            }
          }}
          style={{
            padding: '12px 24px',
            background: '#333',
            border: 'none',
            borderRadius: '0',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          Continue
        </button>
      ) : (
        <button
          type="submit"
          disabled={isProcessing}
          style={{
            padding: '12px 24px',
            background: isProcessing ? '#ccc' : '#333',
            border: 'none',
            borderRadius: '0',
            color: '#fff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            opacity: isProcessing ? 0.7 : 1
          }}
        >
          {isProcessing ? 'PROCESSING...' : 'Place Order'}
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile-specific styles */}
      <style>{`
        @media (max-width: 768px) {
          .checkout-mobile-container {
            padding: 20px 16px 80px !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          .checkout-step-content {
            padding: 0 20px !important;
            max-width: 100% !important;
          }
          .checkout-form-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .checkout-input {
            font-size: 16px !important; /* Prevent zoom on iOS */
          }
        }
      `}</style>
      
      <Header />
      
      <div 
        className={isMobile ? 'checkout-mobile-container' : ''}
        style={{
          maxWidth: isMobile ? '100%' : '1200px',
          margin: '0 auto',
          padding: isMobile ? '20px 16px 80px' : '40px 24px 80px',
          boxSizing: 'border-box'
        }}>
        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/cart')}
          style={{
            background: 'none',
            border: 'none',
            color: '#666',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '32px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          ← Back to Cart
        </button>
        
        {/* Page Title */}
        {!isMobile && (
          <>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '600',
              margin: '0 0 8px 0',
              color: '#181818',
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              Checkout
            </h1>
            
            <div style={{
              height: '2px',
              background: '#e8e8e8',
              margin: '0 0 48px 0'
            }} />
          </>
        )}
        
        {/* Mobile Steps or Desktop Form */}
        {isMobile ? (
          <>
            <StepProgressBar />
            <form onSubmit={handleSubmit}>
              {/* Step 1: Contact Information */}
              {currentStep === 1 && (
                <div className="checkout-step-content">
                  <StepHeader 
                    title="Contact Information"
                    subtitle="We'll use this to contact you about your order"
                  />
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <div>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Full Name*"
                        className="checkout-input"
                        style={{
                          width: '100%',
                          padding: '16px 0',
                          border: 'none',
                          borderBottom: '2px solid #e8e8e8',
                          borderRadius: '0',
                          fontSize: '16px',
                          background: 'transparent',
                          color: '#333',
                          outline: 'none',
                          transition: 'border-color 0.2s ease',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                          ...(errors.fullName ? { borderBottomColor: '#E53E3E' } : {}),
                          ...(formData.fullName.length >= 2 ? { borderBottomColor: '#4CAF50' } : {})
                        }}
                        maxLength={50}
                      />
                      {errors.fullName && (
                        <span style={{
                          fontSize: '12px',
                          color: '#E53E3E',
                          marginTop: '8px',
                          display: 'block'
                        }}>
                          {errors.fullName}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email Address*"
                        className="checkout-input"
                        style={{
                          width: '100%',
                          padding: '16px 0',
                          border: 'none',
                          borderBottom: '2px solid #e8e8e8',
                          borderRadius: '0',
                          fontSize: '16px',
                          background: 'transparent',
                          color: '#333',
                          outline: 'none',
                          transition: 'border-color 0.2s ease',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                          ...(errors.email ? { borderBottomColor: '#E53E3E' } : {}),
                          ...(validateEmailReal(formData.email) ? { borderBottomColor: '#4CAF50' } : {})
                        }}
                        maxLength={100}
                      />
                      {errors.email && (
                        <span style={{
                          fontSize: '12px',
                          color: '#E53E3E',
                          marginTop: '8px',
                          display: 'block'
                        }}>
                          {errors.email}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone Number* (+974)"
                        className="checkout-input"
                        style={{
                          width: '100%',
                          padding: '16px 0',
                          border: 'none',
                          borderBottom: '2px solid #e8e8e8',
                          borderRadius: '0',
                          fontSize: '16px',
                          background: 'transparent',
                          color: '#333',
                          outline: 'none',
                          transition: 'border-color 0.2s ease',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                          ...(errors.phone ? { borderBottomColor: '#E53E3E' } : {}),
                          ...(formData.phone.length >= 8 ? { borderBottomColor: '#4CAF50' } : {})
                        }}
                        maxLength={20}
                      />
                      {errors.phone && (
                        <span style={{
                          fontSize: '12px',
                          color: '#E53E3E',
                          marginTop: '8px',
                          display: 'block'
                        }}>
                          {errors.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Delivery Options */}
              {currentStep === 2 && (
                <div className="checkout-step-content">
                  <StepHeader 
                    title="Delivery Options"
                    subtitle="How would you like to receive your order?"
                  />
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        border: formData.deliveryMethod === 'delivery' ? '2px solid #333' : '2px solid #e8e8e8',
                        background: formData.deliveryMethod === 'delivery' ? '#f8f8f8' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="delivery"
                          checked={formData.deliveryMethod === 'delivery'}
                          onChange={handleInputChange}
                          style={{ marginRight: '12px' }}
                        />
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                            Delivery
                          </div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {deliverySettings.deliveryFee > 0 ? `QAR ${deliverySettings.deliveryFee} delivery fee` : 'Free delivery'}
                            {totalPrice >= deliverySettings.freeDeliveryThreshold && ` (Free on orders QAR ${deliverySettings.freeDeliveryThreshold} and above)`}
                          </div>
                        </div>
                      </label>
                      
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        border: formData.deliveryMethod === 'pickup' ? '2px solid #333' : '2px solid #e8e8e8',
                        background: formData.deliveryMethod === 'pickup' ? '#f8f8f8' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="pickup"
                          checked={formData.deliveryMethod === 'pickup'}
                          onChange={handleInputChange}
                          style={{ marginRight: '12px' }}
                        />
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                            Pickup
                          </div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            Pick up from one of our locations
                          </div>
                        </div>
                      </label>
                    </div>
                    
                    {formData.deliveryMethod === 'pickup' && (
                      <select
                        name="pickupBranch"
                        value={formData.pickupBranch}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '16px',
                          border: '2px solid #e8e8e8',
                          borderRadius: '0',
                          fontSize: '16px',
                          background: '#fff',
                          color: '#333',
                          outline: 'none',
                          fontFamily: 'inherit',
                          ...(errors.pickupBranch ? { borderColor: '#E53E3E' } : {})
                        }}
                      >
                        <option value="">Select pickup location</option>
                        <option value="Al Wakrah">Al Wakrah Branch</option>
                        <option value="Gewan Island">Gewan Island Branch</option>
                      </select>
                    )}
                    {errors.pickupBranch && (
                      <span style={{
                        fontSize: '12px',
                        color: '#E53E3E',
                        marginTop: '8px',
                        display: 'block'
                      }}>
                        {errors.pickupBranch}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Payment Method */}
              {currentStep === 3 && (
                <div className="checkout-step-content">
                  <StepHeader 
                    title="Payment Method"
                    subtitle="Choose how you'd like to pay"
                  />
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      border: formData.paymentMethod === 'Online Payment' ? '2px solid #333' : '2px solid #e8e8e8',
                      background: formData.paymentMethod === 'Online Payment' ? '#f8f8f8' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Online Payment"
                        checked={formData.paymentMethod === 'Online Payment'}
                        onChange={handleInputChange}
                        style={{ marginRight: '12px' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                          Online Payment
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Pay securely with card
                        </div>
                      </div>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      border: formData.paymentMethod === 'Cash on Delivery' ? '2px solid #333' : '2px solid #e8e8e8',
                      background: formData.paymentMethod === 'Cash on Delivery' ? '#f8f8f8' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Cash on Delivery"
                        checked={formData.paymentMethod === 'Cash on Delivery'}
                        onChange={handleInputChange}
                        style={{ marginRight: '12px' }}
                      />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
                          Cash on Delivery
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Pay when you receive your order
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  <div style={{ marginTop: '24px' }}>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      placeholder="Special instructions (optional)"
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '16px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '0',
                        fontSize: '16px',
                        background: '#fff',
                        color: '#333',
                        outline: 'none',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                      maxLength={200}
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Order Review */}
              {currentStep === 4 && (
                <div className="checkout-step-content">
                  <StepHeader 
                    title="Order Review"
                    subtitle="Review your order before placing it"
                  />
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Order Items */}
                    <div style={{
                      background: '#f8f8f8',
                      padding: '20px',
                      borderRadius: '0'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 16px 0',
                        color: '#181818'
                      }}>
                        Your Order ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
                      </h3>
                      
                      {cartItems.map((item, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingBottom: '12px',
                          marginBottom: '12px',
                          borderBottom: index < cartItems.length - 1 ? '1px solid #e8e8e8' : 'none'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              {item.name} × {item.quantity}
                            </div>
                            {(item.selectedWeight || item.selectedBeanType) && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                {[item.weightDisplay && item.weightDisplay !== 'Standard' ? item.weightDisplay : (item.selectedWeight?.label || item.selectedWeight), item.selectedBeanType].filter(Boolean).join(' - ')}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                            QAR {(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                      ))}
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '2px solid #e8e8e8'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          Subtotal
                        </span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          QAR {totalPrice.toFixed(2)}
                        </span>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '8px'
                      }}>
                        <span style={{ fontSize: '14px', color: '#666' }}>
                          {formData.deliveryMethod === 'delivery' ? 'Delivery' : 'Pickup'}
                        </span>
                        <span style={{ fontSize: '14px', color: '#666' }}>
                          {deliveryFee === 0 ? 'FREE' : `QAR ${deliveryFee.toFixed(2)}`}
                        </span>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '2px solid #333'
                      }}>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
                          Total
                        </span>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
                          QAR {(totalPrice + deliveryFee).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Contact & Delivery Info */}
                    <div style={{
                      background: '#f8f8f8',
                      padding: '20px',
                      borderRadius: '0'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 16px 0',
                        color: '#181818'
                      }}>
                        Contact & Delivery
                      </h3>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontSize: '14px', color: '#333' }}>
                          <strong>{formData.fullName}</strong>
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {formData.email}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {formData.phone}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {formData.deliveryMethod === 'delivery' ? 'Delivery' : `Pickup from ${formData.pickupBranch}`}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Payment: {formData.paymentMethod}
                        </div>
                        {formData.comment && (
                          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                            <strong>Note:</strong> {formData.comment}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <StepButtons />
            </form>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '48px',
              alignItems: 'flex-start'
            }}>
            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
              
              {/* Contact Information */}
              <section>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 24px 0',
                  color: '#181818',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Contact Information
                </h2>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px',
                  marginBottom: '24px'
                }}>
                  <div>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Full Name*"
                      style={{
                        width: '100%',
                        padding: '16px 0',
                        border: 'none',
                        borderBottom: '1px solid #e8e8e8',
                        borderRadius: '0',
                        fontSize: '16px',
                        background: 'transparent',
                        color: '#333',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        fontFamily: 'inherit',
                        ...(errors.fullName ? { borderBottomColor: '#E53E3E' } : {}),
                        ...(formData.fullName.length >= 2 ? { borderBottomColor: '#4CAF50' } : {})
                      }}
                      maxLength={50}
                    />
                    {errors.fullName && (
                      <span style={{
                        fontSize: '12px',
                        color: '#E53E3E',
                        marginTop: '4px',
                        display: 'block'
                      }}>
                        {errors.fullName}
                      </span>
                    )}
                  </div>
                  
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address*"
                      style={{
                        width: '100%',
                        padding: '16px 0',
                        border: 'none',
                        borderBottom: '1px solid #e8e8e8',
                        borderRadius: '0',
                        fontSize: '16px',
                        background: 'transparent',
                        color: '#333',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        fontFamily: 'inherit',
                        ...(errors.email ? { borderBottomColor: '#E53E3E' } : {}),
                        ...(validateEmailReal(formData.email) ? { borderBottomColor: '#4CAF50' } : {})
                      }}
                      maxLength={100}
                    />
                    {errors.email && (
                      <span style={{
                        fontSize: '12px',
                        color: '#E53E3E',
                        marginTop: '4px',
                        display: 'block'
                      }}>
                        {errors.email}
                      </span>
                    )}
                  </div>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '24px'
                }}>
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: '1px solid #e8e8e8',
                      paddingBottom: '16px'
                    }}>
                      <span style={{
                        padding: '0 12px 0 0',
                        color: '#666',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}>
                        +974
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="XXXX XXXX"
                        style={{
                          border: 'none',
                          borderBottom: 'none',
                          padding: '0',
                          flex: 1,
                          fontSize: '16px',
                          background: 'transparent',
                          color: '#333',
                          outline: 'none',
                          fontFamily: 'inherit'
                        }}
                        maxLength={9}
                      />
                    </div>
                    {errors.phone && (
                      <span style={{
                        fontSize: '12px',
                        color: '#E53E3E',
                        marginTop: '4px',
                        display: 'block'
                      }}>
                        {errors.phone}
                      </span>
                    )}
                  </div>
                </div>
              </section>
              
              {/* Delivery Method */}
              <section>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 24px 0',
                  color: '#181818',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Delivery Method
                </h2>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="delivery"
                      checked={formData.deliveryMethod === 'delivery'}
                      onChange={handleInputChange}
                      style={{ marginRight: '16px' }}
                    />
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        GREY Delivery
                      </span>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: totalPrice >= deliverySettings.freeDeliveryThreshold ? '#4CAF50' : '#333'
                      }}>
                        {totalPrice >= deliverySettings.freeDeliveryThreshold ? 'Free' : `QAR ${deliverySettings.deliveryFee.toFixed(2)}`}
                      </span>
                    </div>
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="pickup"
                      checked={formData.deliveryMethod === 'pickup'}
                      onChange={handleInputChange}
                      style={{ marginRight: '16px' }}
                    />
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Pick up from Store
                      </span>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#4CAF50'
                      }}>
                        Free
                      </span>
                    </div>
                  </label>

                  {formData.deliveryMethod === 'pickup' && (
                    <div style={{
                      marginTop: '16px',
                      padding: '24px',
                      background: '#f8f8f8',
                      border: '1px solid #e8e8e8'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 16px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Choose Pickup Location
                      </h3>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          padding: '16px',
                          background: '#fff',
                          border: '1px solid #e8e8e8'
                        }}>
                          <input
                            type="radio"
                            name="pickupBranch"
                            value="al_wakrah"
                            checked={formData.pickupBranch === 'al_wakrah'}
                            onChange={handleInputChange}
                            style={{ marginRight: '12px', marginTop: '2px' }}
                          />
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '4px'
                            }}>
                              Al Wakrah Branch
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#666',
                              marginBottom: '4px'
                            }}>
                              Building 37, Mesaieed St., Al Wakrah
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#4CAF50',
                              fontWeight: '600'
                            }}>
                              Ready in: 2-3 hours
                            </div>
                          </div>
                        </label>
                        
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          padding: '16px',
                          background: '#fff',
                          border: '1px solid #e8e8e8'
                        }}>
                          <input
                            type="radio"
                            name="pickupBranch"
                            value="gewan_island"
                            checked={formData.pickupBranch === 'gewan_island'}
                            onChange={handleInputChange}
                            style={{ marginRight: '12px', marginTop: '2px' }}
                          />
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '4px'
                            }}>
                              Gewan Island Branch
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: '#666',
                              marginBottom: '4px'
                            }}>
                              Building 06, Crystal 5, Gewan Island
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#4CAF50',
                              fontWeight: '600'
                            }}>
                              Ready in: 1-2 hours
                            </div>
                          </div>
                        </label>
                      </div>
                      
                      {errors.pickupBranch && (
                        <span style={{
                          fontSize: '12px',
                          color: '#E53E3E',
                          marginTop: '8px',
                          display: 'block'
                        }}>
                          {errors.pickupBranch}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </section>
              
              {/* Payment Method */}
              <section>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 24px 0',
                  color: '#181818',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Payment Method
                </h2>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="Online Payment"
                      checked={formData.paymentMethod === "Online Payment"}
                      onChange={handleInputChange}
                      style={{ marginRight: '16px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '4px'
                      }}>
                        Online Payment
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#666'
                      }}>
                        Secure payment with credit/debit card or digital wallet
                      </div>
                    </div>
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="Cash On Delivery"
                      checked={formData.paymentMethod === "Cash On Delivery"}
                      onChange={handleInputChange}
                      style={{ marginRight: '16px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '4px'
                      }}>
                        Cash on Delivery
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#666'
                      }}>
                        Pay with cash when your order arrives
                      </div>
                    </div>
                  </label>
                </div>
              </section>
              
              {/* Comments */}
              <section>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 24px 0',
                  color: '#181818',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Order Notes
                </h2>
                
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  placeholder="Special instructions, delivery notes, or comments..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #e8e8e8',
                    borderRadius: '0',
                    fontSize: '16px',
                    background: '#fff',
                    color: '#333',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: 1.5
                  }}
                  maxLength={500}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#888',
                  textAlign: 'right',
                  marginTop: '8px'
                }}>
                  {formData.comment.length}/500
                </div>
              </section>
            </div>
            
            {/* Order Summary */}
            <div style={{
              background: '#f8f8f8',
              padding: '32px',
              border: '1px solid #e8e8e8',
              position: 'sticky',
              top: '20px'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                margin: '0 0 24px 0',
                color: '#181818',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Order Summary
              </h2>
              
              {/* Cart Items */}
              <div style={{ marginBottom: '24px' }}>
                {cartItems.map((item, index) => {
                  let variant = '';
                  if (item.weightDisplay && item.weightDisplay !== 'Standard') {
                    variant += item.weightDisplay;
                  } else if (item.selectedWeight) {
                    if (typeof item.selectedWeight === 'object' && item.selectedWeight.label) {
                      variant += item.selectedWeight.label;
                    } else if (typeof item.selectedWeight === 'string') {
                      variant += item.selectedWeight;
                    }
                  }
                  if (item.selectedBeanType) {
                    if (variant) variant += ' - ';
                    variant += item.selectedBeanType;
                  }
                  
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '16px',
                      borderBottom: index < cartItems.length - 1 ? '1px solid #e8e8e8' : 'none'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: '#fff',
                        border: '1px solid #e8e8e8',
                        marginRight: '12px',
                        flexShrink: 0,
                        overflow: 'hidden'
                      }}>
                        <img src={item.image} alt={item.name} style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#181818',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {item.name}
                        </div>
                        {variant && (
                          <div style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '4px',
                            fontStyle: 'italic'
                          }}>
                            {variant}
                          </div>
                        )}
                        <div style={{
                          fontSize: '12px',
                          color: '#888'
                        }}>
                          Qty: {item.quantity}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#181818',
                          marginTop: '4px'
                        }}>
                          QAR {(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Summary Totals */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Subtotal</span>
                  <span style={{ fontSize: '14px', color: '#181818' }}>QAR {totalPrice.toFixed(2)}</span>
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {formData.deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}
                  </span>
                  <span style={{ fontSize: '14px', color: '#181818' }}>
                    {formData.deliveryMethod === 'pickup' ? 'Free' : (deliveryFee === 0 ? 'Free' : `QAR ${deliveryFee.toFixed(2)}`)}
                  </span>
                </div>
                
                <div style={{
                  height: '1px',
                  background: '#e8e8e8',
                  margin: '16px 0'
                }} />
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '24px'
                }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#181818',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Total
                  </span>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#181818',
                    letterSpacing: '0.5px'
                  }}>
                    QAR {(totalPrice + deliveryFee).toFixed(2)}
                  </span>
                </div>
                
                {/* Place Order Button */}
                <button 
                  type="submit" 
                  disabled={isProcessing || loading}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: (isProcessing || loading) ? '#ccc' : '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: (isProcessing || loading) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: (isProcessing || loading) ? 0.7 : 1
                  }}
                >
                  {isProcessing ? 'PROCESSING...' : (loading ? 'Processing Order...' : 'PLACE ORDER')}
                </button>
                
                {/* Security Note */}
                <p style={{
                  fontSize: '12px',
                  color: '#888',
                  textAlign: 'center',
                  margin: '16px 0 0 0',
                  fontStyle: 'italic'
                }}>
                  Secure checkout with SSL encryption
                </p>
              </div>
              
              {errors.form && (
                <div style={{
                  color: '#E53E3E',
                  fontSize: '14px',
                  marginTop: '16px',
                  textAlign: 'center',
                  padding: '12px',
                  background: 'rgba(229, 62, 62, 0.1)',
                  border: '1px solid rgba(229, 62, 62, 0.2)'
                }}>
                  {errors.form}
                </div>
              )}
            </div>
          </div>
        </form>
        )}
      </div>
      
      <Footer />
    </>
  );
};

export default CheckoutPage;